/**
 * ActiveScopeChip
 *
 * Tiny banner shown at the top of Gamify / Flashcards / Quiz / Library
 * pages explaining WHY the listing is filtered to a specific subject +
 * chapter. Lets the student "Clear filter" to broaden the view (useful
 * when revising across chapters) without touching the global top-bar
 * dropdown selection (which keeps the chat tutor scoped correctly).
 *
 * Props:
 *   subject (string, optional)  — currently scoped subject
 *   topic   (string, optional)  — currently scoped chapter
 *   onClear (function)          — called when the student taps "Clear filter"
 *
 * Renders nothing when neither subject nor topic is set.
 */
import { X, Filter } from 'lucide-react';

export default function ActiveScopeChip({ subject, topic, onClear }) {
  const subj = (subject || '').trim();
  const top = (topic || '').trim();
  if (!subj && !top) return null;
  return (
    <div
      className="flex items-center gap-2 flex-wrap rounded-2xl bg-indigo-50/70 border border-indigo-100 px-3 py-2 mb-4"
      data-testid="active-scope-chip">
      <Filter className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
      <p className="text-[11px] font-body text-indigo-700 flex-1 min-w-0">
        Showing only <strong className="font-heading">{subj || 'all subjects'}</strong>
        {top && (
          <>
            {' · '}
            <strong className="font-heading">{top}</strong>
          </>
        )}
        <span className="text-indigo-400/80 ml-1">
          — content scoped to what you picked in the top bar.
        </span>
      </p>
      <button
        onClick={onClear}
        className="inline-flex items-center gap-1 text-[10px] font-heading uppercase tracking-wider text-indigo-700 hover:text-indigo-900 rounded-full px-2 py-0.5 hover:bg-indigo-100 transition-colors"
        data-testid="active-scope-chip-clear">
        <X className="w-3 h-3" />
        Clear filter
      </button>
    </div>
  );
}
